import {
    ColumnDef,
    ExpandedState,
    functionalUpdate,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getGroupedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    OnChangeFn,
    Row,
    Table,
    TableOptions,
    TableState,
    Updater,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';

import { CLIENT_FILTER_FN_FOR_TYPE } from './columnFilterFns';
import { DataTableOptions } from './types';

// Accept any filter value without client-side filtering; the server owns data operations.
const serverSideFilterFn = () => true;

// Header chrome a column can't shrink below: cell padding, a few label characters, sort icon, filter button.
const HEADER_PADDING = 32;
const MIN_LABEL_WIDTH = 32;
const SORT_ICON_WIDTH = 18;
const FILTER_BUTTON_WIDTH = 28;

export function isDataColumn<T>(column: ColumnDef<T, any>): boolean {
    return 'accessorKey' in column || 'accessorFn' in column;
}

function withHeaderMinSize<T>(column: ColumnDef<T, any>): ColumnDef<T, any> {
    if (column.minSize !== undefined || !isDataColumn(column)) return column;
    const canSort = column.enableSorting !== false;
    const canFilter = column.enableColumnFilter !== false;
    return {
        ...column,
        minSize:
            HEADER_PADDING + MIN_LABEL_WIDTH + (canSort ? SORT_ICON_WIDTH : 0) + (canFilter ? FILTER_BUTTON_WIDTH : 0),
    };
}

// Rows cache values per column id, so each `groupBy` function gets a fresh hidden column to group on.
const GROUP_COLUMN_PREFIX = '__group';
let nextGroupColumnId = 0;

function groupColumn<T>(id: string, groupBy: (row: T) => string): ColumnDef<T, any> {
    return {
        id,
        accessorFn: groupBy,
        enableHiding: false,
        enableSorting: false,
        enableColumnFilter: false,
        meta: { isDisplayCol: true },
    };
}

// Group rows start expanded: for them an `expanded` entry means collapsed. Toggling still goes through
// TanStack's `toggleExpanded`, and group row ids contain the group key, so a new grouping starts expanded.
function isRowExpanded<T>(row: Row<T>, expanded: ExpandedState): boolean {
    if (expanded === true) return true;
    return row.getIsGrouped() ? !expanded[row.id] : !!expanded[row.id];
}

export function isPaginationEnabled<T>(options: DataTableOptions<T>): boolean {
    return options.mode === 'server' || options.paginate === true;
}

// Size 0 makes getSize() fall back to the column's minimum width until it is resized.
function withContentSizing<T>(
    column: ColumnDef<T, any>,
    defaultSize: number | undefined,
    defaultMinSize: number | undefined,
): ColumnDef<T, any> {
    if (column.size !== undefined || defaultSize !== undefined) return column;
    return {
        ...column,
        size: 0,
        minSize: column.minSize ?? defaultMinSize ?? 0,
        meta: { ...column.meta, sizeToContent: true },
    };
}

function splitOptions<T>(options: DataTableOptions<T>) {
    if (options.mode === 'client') {
        const { mode, columns, paginate, columnWidths = 'content', groupBy, ...tableOptions } = options;
        const modeOptions: Partial<TableOptions<T>> = {
            getSortedRowModel: getSortedRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            getGroupedRowModel: getGroupedRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            manualPagination: !paginate,
        };
        return { mode, columns, columnWidths, groupBy, tableOptions, modeOptions };
    }
    const { mode, columns, columnWidths = 'fixed', ...tableOptions } = options;
    const modeOptions: Partial<TableOptions<T>> = {
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
    };
    return { mode, columns, columnWidths, groupBy: undefined, tableOptions, modeOptions };
}

/** Builds the TanStack table behind the data tables; use directly to compose the renderer, pagination, and toolbars. */
export function useDataTable<T>(options: DataTableOptions<T>): Table<T> {
    const { mode, columns, columnWidths, groupBy, tableOptions, modeOptions } = splitOptions(options);
    const defaultSize = tableOptions.defaultColumn?.size;
    const defaultMinSize = tableOptions.defaultColumn?.minSize;

    // Server tables filter every column (the backend interprets the value); client tables
    // only filter columns whose `meta.filterType` maps to an in-memory filter function.
    const resolvedColumns = useMemo<ColumnDef<T, any>[]>(
        () =>
            columns.map((column) => {
                const filterType = column.meta?.filterType;
                const withFilter: ColumnDef<T, any> =
                    mode === 'server'
                        ? {
                              ...column,
                              enableColumnFilter: column.enableColumnFilter ?? true,
                              filterFn: serverSideFilterFn,
                          }
                        : {
                              ...column,
                              enableColumnFilter: column.enableColumnFilter ?? !!filterType,
                              ...(!column.filterFn &&
                                  filterType && { filterFn: CLIENT_FILTER_FN_FOR_TYPE[filterType] }),
                          };
                const withMinSize = withHeaderMinSize(withFilter);
                return columnWidths === 'content'
                    ? withContentSizing(withMinSize, defaultSize, defaultMinSize)
                    : withMinSize;
            }),
        [columns, mode, columnWidths, defaultSize, defaultMinSize],
    );
    const groupingOptions = useGroupingOptions(groupBy, tableOptions);

    const table = useReactTable<T>({
        columnResizeMode: 'onChange',
        enableColumnResizing: true,
        ...tableOptions,
        columns: useMemo(
            () => [...groupingOptions.columns, ...resolvedColumns],
            [groupingOptions.columns, resolvedColumns],
        ),
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        ...modeOptions,
        ...groupingOptions.options,
    });
    groupingOptions.tableRef.current = table;
    return table;
}

// Internal entries are stripped before reaching the caller, so they never end up in persisted settings.
function useOverridableState<S>(
    controlled: S | undefined,
    initial: S,
    onChange: OnChangeFn<S> | undefined,
    withInternal: (state: S) => S,
    withoutInternal: (state: S) => S,
) {
    const [own, setOwn] = useState<S>(initial);
    const state = controlled ?? own;
    const setState = (updater: Updater<S>) => {
        const callerUpdater = (old: S) => withoutInternal(functionalUpdate(updater, withInternal(old)));
        setOwn(callerUpdater);
        onChange?.(callerUpdater);
    };
    return [withInternal(state), setState] as const;
}

function omitKey<S extends Record<string, boolean>>(state: S, key: string | undefined): S {
    if (key === undefined || !(key in state)) return state;
    const { [key]: _omitted, ...rest } = state;
    return rest as S;
}

function useGroupingOptions<T>(groupBy: ((row: T) => string) | undefined, tableOptions: Partial<TableOptions<T>>) {
    const tableRef = useRef<Table<T>>();
    // Memoized as a whole: a new `grouping` array would regroup the rows, and TanStack then resets `expanded`.
    const { groupColumnId, columns, grouping } = useMemo(() => {
        if (!groupBy) return { groupColumnId: undefined, columns: [], grouping: [] };
        const id = `${GROUP_COLUMN_PREFIX}${nextGroupColumnId++}`;
        return { groupColumnId: id, columns: [groupColumn(id, groupBy)], grouping: [id] };
    }, [groupBy]);
    const state: Partial<TableState> | undefined = tableOptions.state;
    const [columnVisibility, setColumnVisibility] = useOverridableState<VisibilityState>(
        state?.columnVisibility,
        tableOptions.initialState?.columnVisibility ?? {},
        tableOptions.onColumnVisibilityChange,
        (visibility) => (groupColumnId ? { ...visibility, [groupColumnId]: false } : visibility),
        (visibility) => omitKey(visibility, groupColumnId),
    );
    // TanStack skips row expansion while `expanded` has no entries, so grouping adds one that matches no row.
    const [expanded, setExpanded] = useOverridableState<ExpandedState>(
        state?.expanded,
        tableOptions.initialState?.expanded ?? {},
        tableOptions.onExpandedChange,
        (current) => (groupColumnId && current !== true ? { ...current, [groupColumnId]: true } : current),
        (current) => (current === true ? current : omitKey(current, groupColumnId)),
    );
    if (!groupColumnId) return { tableRef, columns, options: {} };

    const options: Partial<TableOptions<T>> = {
        state: { ...state, grouping, columnVisibility, expanded },
        onColumnVisibilityChange: setColumnVisibility,
        onExpandedChange: setExpanded,
        getIsRowExpanded: (row) =>
            (row.getIsGrouped() ? undefined : tableOptions.getIsRowExpanded?.(row)) ??
            isRowExpanded(row, tableRef.current?.getState().expanded ?? {}),
    };
    return { tableRef, columns, options };
}
